import { useEventListener } from '@vueuse/core';
import { isHtmlElement } from '@/js/helpers/type-guards/isHtmlElement';
import { queryWithin } from '@/js/helpers/queryWithin';

const ACTIVE_TAB_PANEL_CLASS = 'tab-wrapper__tab-panel--active';
const ACTIVE_TAB_BUTTON_CLASS = 'tab-wrapper__button--active';
const TAB_BUTTON_SELECTOR = '.tab-wrapper__button';
const TAB_PANEL_SELECTOR_PREFIX = '#tab-';
const TAB_WRAPPER_SELECTOR = '.tab-wrapper';

const hideActiveTab = (tabWrapper: HTMLElement) => {
    const activeTabPanel = queryWithin(tabWrapper, `.${ACTIVE_TAB_PANEL_CLASS}`);
    const activeTabButton = queryWithin<HTMLButtonElement>(tabWrapper, `${TAB_BUTTON_SELECTOR}[aria-selected=true]`);

    if (activeTabButton) {
        activeTabButton.ariaSelected = 'false';
        activeTabButton.classList.remove(ACTIVE_TAB_BUTTON_CLASS);
    }

    activeTabPanel?.classList.remove(ACTIVE_TAB_PANEL_CLASS);
};

const showTab = (tabWrapper: HTMLElement, tabButton: HTMLButtonElement, tabIndex: number) => {
    const tabPanel = queryWithin(tabWrapper, `${TAB_PANEL_SELECTOR_PREFIX}${tabIndex}`);

    hideActiveTab(tabWrapper);

    tabButton.ariaSelected = 'true';
    tabButton.classList.add(ACTIVE_TAB_BUTTON_CLASS);
    tabPanel?.classList.add(ACTIVE_TAB_PANEL_CLASS);
};

const openFirstTab = (tabWrapper: HTMLElement) => {
    const firstTabButton = queryWithin<HTMLButtonElement>(tabWrapper, TAB_BUTTON_SELECTOR);
    const firstTabIndex = Number(firstTabButton?.dataset.tabIndex) || 1;

    showTab(tabWrapper, firstTabButton, firstTabIndex);
};

const addButtonEvents = (tabWrapper: HTMLElement) => {
    useEventListener(
        tabWrapper,
        'click',
        (event: Event) => {
            if (!isHtmlElement(event.target)) {
                return;
            }

            const tabButton: HTMLButtonElement = (event.target).closest(TAB_BUTTON_SELECTOR);

            if (tabButton) {
                const tabIndex = Number(tabButton.dataset.tabIndex);
                showTab(tabWrapper, tabButton, tabIndex);
            }
        },
    );
};

const setupTabs = (tabWrapper: HTMLElement) => {
    addButtonEvents(tabWrapper);
    openFirstTab(tabWrapper);
};

const initializeTabWrappers = () => {
    const tabWrappers = document.querySelectorAll<HTMLElement>(TAB_WRAPPER_SELECTOR);

    tabWrappers.forEach(setupTabs);
};

useEventListener(
    document,
    'DOMContentLoaded',
    initializeTabWrappers,
);
